/**
 * BP-025 Session 2 · AI Acquisition Coach page — `/v3/coach`.
 *
 * Deterministic layer is the source of truth. LLM narration is grounded
 * and cited. Every claim carries a BP + version + evidence path.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles,
  RefreshCw,
  ArrowRight,
  Send,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  ClipboardList,
  Loader2,
  BadgeCheck,
} from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";

const BP_LABELS = {
  "BP-000": "Property",
  "BP-003": "Repair",
  "BP-004": "ARV",
  "BP-005": "Deal Score",
  "BP-014": "Strategy",
  "BP-016": "Lead Score",
  "BP-020": "KPI",
  "BP-022": "Buyer",
  "BP-023": "Portfolio",
  "BP-024": "Market",
};

/** Addresses may arrive as a string OR as {street, unit, city, state, zip_code}. */
function displayAddress(a) {
  if (!a) return "";
  if (typeof a === "string") return a;
  const parts = [a.street, a.unit, a.city, a.state, a.zip_code || a.zip]
    .filter(Boolean);
  return parts.join(", ");
}

const CONFIDENCE_COLOR = (c) =>
  c == null
    ? "text-slate-500"
    : c >= 0.75
    ? "text-emerald-600"
    : c >= 0.55
    ? "text-blue-600"
    : "text-amber-600";

function formatValue(v, fmt) {
  if (v == null) return "—";
  if (fmt === "currency") return `$${Number(v).toLocaleString()}`;
  if (fmt === "count") return Number(v).toLocaleString();
  return String(v);
}

// ─── UI atoms ─────────────────────────────────────────────

const EvidencePill = ({ row }) => (
  <div
    data-testid={`evidence-row-${row.source_bp}`}
    className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 hover:bg-slate-50 transition-colors"
  >
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-wide text-slate-500">{row.label}</div>
      <div className="text-sm font-semibold text-slate-900 truncate">
        {typeof row.value === "object"
          ? JSON.stringify(row.value)
          : formatValue(row.value, row.format)}
      </div>
    </div>
    <div className="text-right">
      <div className="text-[10px] font-semibold uppercase text-slate-500">
        {row.source_bp}
      </div>
      <div className="text-[10px] text-slate-400">{row.source_version}</div>
    </div>
  </div>
);

const WhyNotRow = ({ row }) => (
  <div
    data-testid={`why-not-${row.strategy}`}
    className="rounded-lg border border-slate-200 bg-white px-3 py-3"
  >
    <div className="flex items-center justify-between">
      <div className="font-semibold text-slate-800">{row.strategy_label}</div>
      <div className="text-xs text-slate-500">
        Score {row.score ?? "—"} · BP-014
      </div>
    </div>
    <div className="mt-1 text-sm text-slate-600">{row.reason}</div>
  </div>
);

const ConfidenceMeter = ({ conf }) => {
  const pct = Math.round((conf ?? 0) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 rounded-full bg-slate-200 overflow-hidden">
        <div
          className={`h-full ${
            conf >= 0.75
              ? "bg-emerald-500"
              : conf >= 0.55
              ? "bg-blue-500"
              : "bg-amber-500"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-sm font-semibold ${CONFIDENCE_COLOR(conf)}`}>
        {pct}%
      </span>
    </div>
  );
};

// ─── Sections ──────────────────────────────────────────────

function DailyBriefingCard({ briefing, onRefresh, loading }) {
  if (!briefing) return null;
  return (
    <div
      data-testid="coach-briefing"
      className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-300">
            Today · Coach Briefing
          </div>
          <div className="mt-1 text-lg font-semibold">
            {briefing.priorities?.length || 0} priorities · {briefing.for_date}
          </div>
        </div>
        <button
          data-testid="coach-briefing-refresh"
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center gap-1 rounded-lg border border-white/20 px-3 py-1 text-sm hover:bg-white/10"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2">
        {(briefing.priorities || []).slice(0, 5).map((p, i) => (
          <div
            key={i}
            data-testid={`coach-priority-${i}`}
            className="flex items-center justify-between gap-3 rounded-lg bg-white/5 border border-white/10 px-3 py-2"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-xs font-semibold text-slate-300">#{i + 1}</div>
              <div className="truncate">
                {displayAddress(p.address) || p.message || "—"}
              </div>
            </div>
            <div className="text-xs text-slate-300 shrink-0">
              {p.reason || p.kind}
            </div>
          </div>
        ))}
        {(!briefing.priorities || briefing.priorities.length === 0) && (
          <div className="text-sm text-slate-300">
            No priorities today. Track more workspaces or seed more markets.
          </div>
        )}
      </div>
    </div>
  );
}

function OpportunitiesTable({ rows, onSelect }) {
  if (!rows?.length) {
    return (
      <div
        data-testid="coach-opportunities-empty"
        className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500"
      >
        No opportunities yet. Add properties + tracked markets to see rankings.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <table className="w-full text-sm" data-testid="coach-opportunities-table">
        <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="px-4 py-2">Property</th>
            <th className="px-4 py-2">Deal</th>
            <th className="px-4 py-2">Strategy</th>
            <th className="px-4 py-2">Market</th>
            <th className="px-4 py-2">Score</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr
              key={r.property_id}
              data-testid={`opp-row-${r.property_id}`}
              className="hover:bg-slate-50 cursor-pointer"
              onClick={() => onSelect(r)}
            >
              <td className="px-4 py-3 font-medium text-slate-900 truncate max-w-[240px]">
                {displayAddress(r.address) || r.property_id}
              </td>
              <td className="px-4 py-3">{r.deal_score ?? "—"}</td>
              <td className="px-4 py-3">
                {r.strategy_winner ? (
                  <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                    {r.strategy_winner}
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-4 py-3">
                {r.market_tier || r.market_health || "—"}
              </td>
              <td className="px-4 py-3">
                <span className="font-semibold text-slate-900">
                  {r.opportunity_score ?? "—"}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                <ArrowRight className="w-4 h-4 text-slate-400 inline" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AnalysisDrawer({ analysis, narration, onClose, onNarrate, onFeedback, onCreateTask, busy }) {
  if (!analysis) return null;
  const reco = analysis.recommendation || {};
  return (
    <div
      data-testid="coach-analysis-drawer"
      className="fixed inset-y-0 right-0 z-40 w-full max-w-2xl overflow-y-auto bg-white border-l border-slate-200 shadow-2xl"
    >
      <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
        <div>
          <div className="text-xs uppercase text-slate-500">Coach Analysis</div>
          <div className="text-lg font-semibold text-slate-900 truncate max-w-[380px]">
            {displayAddress(analysis.property_address) || analysis.property_id}
          </div>
        </div>
        <button
          data-testid="drawer-close"
          onClick={onClose}
          className="text-slate-500 hover:text-slate-900"
        >
          ✕
        </button>
      </div>

      <div className="p-6 space-y-6">
        {/* Recommendation header */}
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-center gap-2 text-xs uppercase text-emerald-800">
            <BadgeCheck className="w-4 h-4" /> Recommendation
          </div>
          <div
            data-testid="coach-recommendation"
            className="mt-1 text-2xl font-bold text-emerald-900"
          >
            {reco.recommended_label || reco.recommended || "—"}
          </div>
          <div className="mt-3 flex items-center gap-4">
            <div>
              <div className="text-xs text-slate-500">Confidence</div>
              <ConfidenceMeter conf={analysis.confidence} />
            </div>
            <div>
              <div className="text-xs text-slate-500">Engines</div>
              <div
                data-testid="coach-contrib-count"
                className="text-sm font-semibold text-slate-900"
              >
                {analysis.contributing_engines?.length || 0}
              </div>
            </div>
            {reco.engine_divergence && (
              <div className="ml-auto flex items-center gap-1 text-xs text-amber-700">
                <AlertCircle className="w-4 h-4" />
                Engine disagreement
              </div>
            )}
          </div>
        </div>

        {/* Evidence Panel */}
        <section data-testid="coach-evidence-panel">
          <h3 className="text-sm font-semibold text-slate-900 mb-2">
            Evidence · every claim cites a source
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(analysis.evidence || []).map((row, i) => (
              <EvidencePill key={`${row.source_bp}-${i}`} row={row} />
            ))}
          </div>
        </section>

        {/* Missing information */}
        {analysis.missing_information?.length > 0 && (
          <section
            data-testid="coach-missing-info"
            className="rounded-lg border border-amber-200 bg-amber-50 p-3"
          >
            <div className="flex items-center gap-1 text-xs uppercase text-amber-800">
              <AlertCircle className="w-4 h-4" />
              Missing information · confidence capped ≤ 0.60
            </div>
            <ul className="mt-2 text-sm text-amber-900 list-disc list-inside">
              {analysis.missing_information.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Why Not */}
        {reco.why_not?.length > 0 && (
          <section data-testid="coach-why-not">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              Why not the other strategies?
            </h3>
            <div className="space-y-2">
              {reco.why_not.map((row) => (
                <WhyNotRow key={row.strategy} row={row} />
              ))}
            </div>
          </section>
        )}

        {/* LLM Narration */}
        <section>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">
              AI Narration
            </h3>
            <button
              data-testid="coach-narrate-btn"
              onClick={onNarrate}
              disabled={busy}
              className="text-xs rounded-full bg-slate-900 text-white px-3 py-1 flex items-center gap-1 hover:bg-slate-700 disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              {narration ? "Re-narrate" : "Generate"}
            </button>
          </div>

          {narration && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div
                data-testid="coach-narration-headline"
                className="text-sm font-semibold text-slate-900"
              >
                {narration.headline}
              </div>
              {narration.answer && (
                <p className="mt-1 text-sm text-slate-700">{narration.answer}</p>
              )}
              {narration.strengths?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs uppercase text-emerald-700">
                    Strengths
                  </div>
                  <ul className="mt-1 text-sm text-slate-700 list-disc list-inside space-y-1">
                    {narration.strengths.map((s, i) => (
                      <li key={i}>
                        {s.text}
                        <span className="text-xs text-slate-500 ml-1">
                          [{s.evidence_path}]
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {narration.risks?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs uppercase text-amber-700">Risks</div>
                  <ul className="mt-1 text-sm text-slate-700 list-disc list-inside space-y-1">
                    {narration.risks.map((s, i) => (
                      <li key={i}>{s.text}</li>
                    ))}
                  </ul>
                </div>
              )}
              {narration.recommended_actions?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs uppercase text-blue-700">Actions</div>
                  <ul className="mt-1 text-sm text-slate-700 list-disc list-inside space-y-1">
                    {narration.recommended_actions.map((a, i) => (
                      <li key={i}>
                        {a.text}
                        {a.priority && (
                          <span className="ml-1 text-xs rounded-full bg-slate-200 px-1.5">
                            {a.priority}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-3 text-[10px] text-slate-500">
                {narration.model_name} · {narration.prompt_version}
                {narration.cached && " · cached"}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  data-testid="coach-feedback-up"
                  onClick={() => onFeedback("helpful")}
                  className="flex items-center gap-1 rounded-full border border-slate-200 px-2 py-1 text-xs hover:bg-slate-100"
                >
                  <ThumbsUp className="w-3 h-3" /> Helpful
                </button>
                <button
                  data-testid="coach-feedback-down"
                  onClick={() => onFeedback("not_helpful")}
                  className="flex items-center gap-1 rounded-full border border-slate-200 px-2 py-1 text-xs hover:bg-slate-100"
                >
                  <ThumbsDown className="w-3 h-3" /> Not helpful
                </button>
                <button
                  data-testid="coach-create-task-btn"
                  onClick={onCreateTask}
                  className="ml-auto flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-1 text-xs hover:bg-emerald-700"
                >
                  <ClipboardList className="w-3 h-3" /> Convert to Task
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function AskCoachDock({ onAsked }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null);
  const send = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const res = await apiPost("/api/v3/coach/ask", { question: q.trim() });
      setAnswer(res);
      onAsked?.(res);
    } catch (e) {
      toast.error(e?.message || "Coach unavailable");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid="coach-ask-dock"
      className="rounded-xl border border-slate-200 bg-white p-4"
    >
      <div className="flex items-center gap-2">
        <input
          data-testid="coach-ask-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask the coach — 'What's my top priority today?'"
          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
          disabled={busy}
        />
        <button
          data-testid="coach-ask-send"
          onClick={send}
          disabled={busy}
          className="rounded-lg bg-slate-900 text-white px-3 py-2 text-sm hover:bg-slate-700 disabled:opacity-50 flex items-center gap-1"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {busy ? "Thinking…" : "Ask"}
        </button>
      </div>
      {busy && (
        <div
          data-testid="coach-ask-progress"
          className="mt-2 flex items-center gap-2 text-xs text-slate-500"
        >
          <Loader2 className="w-3 h-3 animate-spin" />
          Claude Sonnet 4.6 is grounding your answer in deterministic engine
          output — usually 15-30s.
        </div>
      )}
      {answer && (
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
          <div
            data-testid="coach-ask-answer"
            className="text-sm text-slate-800 whitespace-pre-wrap"
          >
            {answer.answer || answer.headline}
          </div>
          <div className="mt-1 text-[10px] text-slate-500">
            {answer.model_name} · confidence {(answer.confidence * 100 || 0).toFixed(0)}%
            {answer.cached && " · cached"}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────

export default function CoachPage() {
  const [briefing, setBriefing] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [narration, setNarration] = useState(null);
  const [briefingBusy, setBriefingBusy] = useState(false);
  const [narrateBusy, setNarrateBusy] = useState(false);

  const loadBriefing = async (force = false) => {
    setBriefingBusy(true);
    try {
      const b = await apiGet(
        `/api/v3/coach/daily-briefing${force ? "?force=true" : ""}`
      );
      setBriefing(b);
    } catch (e) {
      toast.error(e?.message || "Failed to load briefing");
    } finally {
      setBriefingBusy(false);
    }
  };
  const loadOpps = async () => {
    try {
      const o = await apiGet("/api/v3/coach/opportunities?limit=25");
      setOpportunities(o || []);
    } catch (e) {
      toast.error(e?.message || "Failed to load opportunities");
    }
  };

  useEffect(() => {
    loadBriefing();
    loadOpps();
  }, []);

  const openAnalysis = async (row) => {
    try {
      const a = await apiPost("/api/v3/coach/analyze-property", {
        property_id: row.property_id,
      });
      setSelectedAnalysis(a);
      setNarration(null);
    } catch (e) {
      toast.error(e?.message || "Coach analysis failed");
    }
  };
  const narrate = async () => {
    if (!selectedAnalysis) return;
    setNarrateBusy(true);
    try {
      const n = await apiPost("/api/v3/coach/narrate", {
        analysis_id: selectedAnalysis.id,
      });
      setNarration(n);
      if (n.cached) toast.success("Cached narration");
    } catch (e) {
      toast.error(e?.message || "Narration failed");
    } finally {
      setNarrateBusy(false);
    }
  };
  const giveFeedback = async (rating) => {
    if (!selectedAnalysis) return;
    try {
      await apiPost("/api/v3/coach/feedback", {
        analysis_id: selectedAnalysis.id,
        rating,
      });
      toast.success("Feedback recorded");
    } catch (e) {
      toast.error(e?.message || "Feedback failed");
    }
  };
  const createTask = async () => {
    if (!selectedAnalysis) return;
    try {
      const res = await apiPost("/api/v3/coach/create-task", {
        analysis_id: selectedAnalysis.id,
        priority: "high",
      });
      toast.success("Task created");
      setSelectedAnalysis((a) => ({ ...a, task_id: res.task.id }));
    } catch (e) {
      toast.error(e?.message || "Task create failed");
    }
  };

  return (
    <div data-testid="coach-page" className="p-6 space-y-6 max-w-6xl mx-auto">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500">
          AI Acquisition Coach · BP-025
        </div>
        <h1 className="text-3xl font-bold text-slate-900">Today's Coach</h1>
        <p className="mt-1 text-sm text-slate-600">
          Cross-engine orchestration. Every claim cites its source engine +
          version. Confidence capped when data is incomplete.
        </p>
      </div>

      <AskCoachDock />

      <DailyBriefingCard
        briefing={briefing}
        loading={briefingBusy}
        onRefresh={() => loadBriefing(true)}
      />

      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-2">
          Opportunities
        </h2>
        <OpportunitiesTable rows={opportunities} onSelect={openAnalysis} />
      </div>

      <AnalysisDrawer
        analysis={selectedAnalysis}
        narration={narration}
        onClose={() => {
          setSelectedAnalysis(null);
          setNarration(null);
        }}
        onNarrate={narrate}
        onFeedback={giveFeedback}
        onCreateTask={createTask}
        busy={narrateBusy}
      />
    </div>
  );
}
